import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { OauthProvider } from '../../../../oauth/providers';
import { RequestBodyService } from '../../../services';
import { SalesloftService } from '../../../services/salesloft.service';
import { CommonUtilService } from '../../../services/CommonUtil.service';
import { ReConnectSettingComponent } from '../../re-connect-setting/re-connect-setting.component';
import { AccountAttributesEnum } from '../../../enums';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../confirmation-dialog';
import { SalesloftApiService } from '../../../services/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getErrorMessage } from 'src/app/shared/helpers';
import { SiModalModule } from '../../si-modal';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FilterPipeModule, OrderByPipeModule } from '../../../pipes';
import { ChipsSelectorFormFieldModule } from '../../ui-components/chips-selector-form-field/chips-selector-form-field.module';
import { IndividualAssignmentComponent } from '../individual-assignment/individual-assignment.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  DuplicateResolutionRadioGroupModule
} from '../../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';

@UntilDestroy()
@Component({
  selector: 'app-salesloft',
  templateUrl: './salesloft.component.html',
  styleUrls: ['./salesloft.component.scss'],
  imports: [
    SiModalModule,
    CommonModule,
    MatRadioModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    FontAwesomeModule,
    FilterPipeModule,
    OrderByPipeModule,
    ChipsSelectorFormFieldModule,
    DuplicateResolutionRadioGroupModule,
    IndividualAssignmentComponent,
    MatButtonModule
  ],
  standalone: true,
})
export class SalesloftComponent implements OnInit {
    pages = {
        setCadence: 0,
        setTags: 1,
        duplicates: 2,
        export: 3,
        exportFinished: 4,
        exportEmpty: 5,
        exportFailed: 6,
        requiresReauth: 7
    };
    exportedContactCount = 0;
    warnings = [];
    isShowingWarnings = false;
    busyLabel = 'Loading...';
    busySublabel = '';
    isBusy = true;
    showPreviousButton = false;
    duplicateMatchIds = {};
    duplicateCount = 0;
    duplicateResolution = 'update';
    available_tags: any = [];
    selected_tags: Observable<any[]>;
    public salesLoftInput$ = new Subject<any>();
    public peopleLoading = false;
    @Input() cadences = [];
    exportType = 'salesloft';
    cadenceFilter = '';
    selectedCadence: Set<number> = new Set<number>();
    currentPage = this.pages.setCadence;
    contacts = [];
    showNextButton = true;
    showCancelButton = true;
    exportError = '';
    showRetryButton: boolean = false;
    showCloseButton = false;
    isSalesLoftComponentOpened: boolean = true;
    individualFlow: boolean;
    individualIds = [];
    accuracyTier: string;
    accuracyTierText: string;
    readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;

    constructor(
        private commonUtilService: CommonUtilService,
        private salesloftService: SalesloftService,
        private authenticationService: AuthenticationService,
        private requestBodyService: RequestBodyService,
        private readonly salesloftApiService: SalesloftApiService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<SalesloftComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data: any,
    ) {
        if (this.data['contacts']) {
            this.contacts = this.data['contacts'];
            this.accuracyTier = this.data['accuracyTier'];
            this.accuracyTierText = this.data['accuracyTierText'];
        }
    }
    ngOnInit() {
        this.individualFlow = this.salesloftService.isIndividualAssignment;
        this.executeCurrentStep();
    }

    errorRefreshToken() {
        if (this.isSalesLoftComponentOpened) {
            this.onClose();
            const modalConfig: MatDialogConfig = {
                panelClass: 'modal-lg',
                data: {}
            };
            if (this.data['contacts']) {
                modalConfig.data['provider'] = OauthProvider.SalesLoft;
                modalConfig.data['match_ids'] = this.contacts;
            }
            this.matDialog.open(ReConnectSettingComponent, modalConfig);
        }
    }
    executeCurrentStep() {
        if (this.currentPage === this.pages.setCadence) {
            this.cadenceFilter = '';
            this.setCadence();
            this.showRetryButton = false;
            this.showNextButton = true;
        }
        if (this.currentPage === this.pages.setTags) {
            this.getTags();
        }
        if (this.currentPage === this.pages.duplicates) {
            this.showNextButton = true;
            this.showCloseButton = false;
            this.validateDuplicates();
        } else if (this.currentPage === this.pages.exportEmpty) {
            this.showNextButton = false;
            this.showCloseButton = true;
        }
        if (this.currentPage === 0) {
            this.showPreviousButton = false;
        } else {
            this.showPreviousButton = true;
        }
        if (this.currentPage === this.pages.export) {
            this.export();
        }

        if (this.currentPage === this.pages.exportFinished) {
            this.showCancelButton = false;
            this.showPreviousButton = false;
            this.showCloseButton = true;
            if (this.authenticationService.hasBypassClearSelectionAfterExport() === false) {
                this.contacts = [];
            }
        }
    }
    selectCadence(cid) {
        if (this.selectedCadence.has(cid)) {
            this.selectedCadence.delete(cid);
        } else {
            this.selectedCadence.add(cid);
        }
    }

    rowSelected(cid) {
        return this.selectedCadence.has(cid);
    }

    setCadence() {
        this.busyLabel = 'Discovering Cadences...';
        this.isBusy = true;
        this.salesloftApiService.setCadence().pipe(untilDestroyed(this)).subscribe((response: any) => {
            this.cadences = response;
            this.isBusy = false;
        }, error => {
            this.errorRefreshToken();
        });
    }
    getTags() {
        this.busyLabel = 'Discovering Tags...';
        this.isBusy = false;
        this.selected_tags = concat(
            of([]),
            this.salesLoftInput$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap(() => this.peopleLoading = true),
                switchMap(term => {
                    if (term) {
                        return this.salesloftApiService.getTags().pipe(
                            map(crArr => crArr.filter(cr => cr.name.includes(term))),
                            catchError(() => of([])),
                            tap(() => this.peopleLoading = false)
                        )
                    } else {
                        this.peopleLoading = false;
                        return of([]);
                    }
                })
            )
        );
    }
    // added for 4184
    previous() {
        const currentIndex = this.currentPage;
        let previousIndex = currentIndex - 1;

        if (currentIndex === this.pages.exportEmpty) {
            previousIndex = this.pages.duplicates;
        }

        if (previousIndex = this.pages.setCadence) {
            this.individualFlow = this.salesloftService.isIndividualAssignment;
        }
        this.currentPage = previousIndex;
        this.executeCurrentStep();
    }

    validateDuplicates() {
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        const matchIds = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        this.salesloftApiService.validateDuplicates(matchIds).pipe(untilDestroyed(this)).subscribe((response: any) => {
            const dupeIds = [];
            let dupesCount = 0;
            response.contacts.forEach((item) => {
                if (item['duplicate'] === true) {
                    dupesCount++;
                    dupeIds.push(item);
                }
            });
            this.duplicateMatchIds = dupeIds;
            this.duplicateCount = dupesCount;
            if (dupesCount === 0) {
                this.next();
            } else {
                this.isBusy = false;
            }
        }, (error) => {
            this.next();
        });
    }
    showWarnings() {
        this.isShowingWarnings = !this.isShowingWarnings;
    }
    export() {
        const toBeExported = [];
        this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return toBeExported.push({ id: contact.matchId });
            } else {
                return contact;
            }
        });

        // Skip all duplicates
        if (this.duplicateResolution === 'skip') {
            for (const key in this.duplicateMatchIds) {
                if (this.duplicateMatchIds.hasOwnProperty(key)) {
                    this.commonUtilService.removeMatchId(toBeExported, this.duplicateMatchIds[key]);
                    this.commonUtilService.removeMatchId(this.individualIds, this.duplicateMatchIds[key]);
                }
            }
        }
        if (toBeExported.length === 0) {
            this.currentPage = this.pages.exportEmpty;
            this.isBusy = false;
            this.showNextButton = false;
            this.executeCurrentStep();

            return;
        }
        this.isShowingWarnings = false;
        this.warnings = [];
        // this.busyLabel = 'Exporting ...';
        this.busyLabel = '';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        this.exportedContactCount = toBeExported.length;
        if (this.individualIds.length > 0 && this.salesloftService.isIndividualAssignment === true) {
            this.salesloftApiService.exportV2(this.individualIds, this.duplicateResolution, this.available_tags).pipe(untilDestroyed(this)).subscribe((response: any) => {
                this.requestBodyService.changeRevealMatchesID({ 'type': 'salesloft', 'data': this.individualIds });
                this.warnings = response;
                this.isBusy = false;
                this.showPreviousButton = false;
                this.showNextButton = false;

                this.next();
            }, (error) => {
                const errMsg = getErrorMessage(error, true);
                if (errMsg) {
                    this.exportError = errMsg;
                    this.showRetryButton = true;
                } else {
                    this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                }
                this.currentPage = this.pages.exportFailed;
                this.showNextButton = false;
                this.showPreviousButton = false;
                this.isBusy = false;
                this.showCancelButton = true;
            });

        } else {
            this.salesloftApiService.exportV2Batch(toBeExported, this.duplicateResolution, Array.from(this.selectedCadence), this.available_tags)
            .pipe(untilDestroyed(this))
            .subscribe((response: any) => {
                this.requestBodyService.changeRevealMatchesID({ 'type': 'salesloft', 'data': toBeExported });
                this.warnings = response;
                this.isBusy = false;
                this.showPreviousButton = false;
                this.showNextButton = false;

                this.next();
            }, (error) => {
                const errMsg = getErrorMessage(error, true);
                if (errMsg) {
                    this.exportError = errMsg;
                    this.showRetryButton = true;
                } else {
                    this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                }
                this.currentPage = this.pages.exportFailed;
                this.showNextButton = false;
                this.showPreviousButton = false;
                this.isBusy = false;
                this.showCancelButton = true;
            });
        }
    }

    retryExport() {
        this.currentPage = this.pages.setCadence;
        this.executeCurrentStep();
    }

    next() {
        this.currentPage++;
        this.executeCurrentStep();
    }
    onClose() {
        this.isSalesLoftComponentOpened = false;
        this.salesloftService.isIndividualAssignment = false;
        this.matDialogRef.close();
    }

    toggleFlow($event, val) {
        let filterCadence = this.individualIds.filter(item => item['cadence']).length > 0;
        if (val === false && filterCadence === true) {
            $event.preventDefault();
            const deleteModalData: MatDialogConfig<ConfirmationDialogData> = {
                panelClass: 'modal-md',
                data: {
                    title: 'Confirmation',
                    confirmMessage: 'Changing the flow back to the basic assignment will clear the mapping between cadences and contacts. Are you sure you want to proceed?',
                    isShowButton: true,
                },
            };
            this.matDialog.open(ConfirmationDialogComponent, deleteModalData).afterClosed().subscribe((success: boolean) => {
                if (success) {
                    this.contacts.filter(item => {
                        if (item['selectedCadence']) {
                            delete item['selectedCadence'];
                        }
                    });
                    this.individualIds.filter(item => {
                        if (item['cadence']) {
                            delete item['cadence'];
                        }
                    });
                    this.salesloftService.isIndividualAssignment = val;
                    this.individualFlow = val;
                } else {
                    this.salesloftService.isIndividualAssignment = true;
                    this.individualFlow = true;
                }
            });
        } else {
            this.salesloftService.isIndividualAssignment = val;
            this.individualFlow = val;
            this.selectedCadence.clear();
        }

    }

    onIndividual(event) {
        this.individualIds = event;
    }

    tagRemoved() {
        this.getTags();
    }
}
